import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
} from '@mui/material';
import { AutoAwesome as AutoAwesomeIcon } from '@mui/icons-material';
import { indicatorsAPI, valuationsAPI, macroAPI, forecastAPI, stocksAPI } from '../services/api';
import {
  MacroSummaryChart,
  TopSectorsCards,
  SectorMomentumCards,
  ChartsSection,
  MarketForecastChart,
  AINewsImpactDashboard,
  MacroScoreTrend,
  IndicatorHeatmap,
  RiskAndBreadthSection,
  RiskRewardScatterPlot,
  PillarTrendAnalysis,
  SectorRotationTracker,
  ValuationMomentumMatrix,
  PerformanceAttribution,
  DashboardSkeleton,
} from '../components/dashboard';
import RiskManagementWidget from '../components/dashboard/RiskManagementWidget';

function Dashboard() {
  const { t } = useTranslation();
  const [pillarScores, setPillarScores] = useState([]);
  const [pillarWeights, setPillarWeights] = useState({});
  const [loading, setLoading] = useState(true);
  const [macroScore, setMacroScore] = useState(null);
  const [topSectors, setTopSectors] = useState([]);
  const [bottomSectors, setBottomSectors] = useState([]);
  const [momentum, setMomentum] = useState({ rising_sectors: [], falling_sectors: [] });
  const [marketBreadth, setMarketBreadth] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [forecastDrivers, setForecastDrivers] = useState(null);
  const [allSectors, setAllSectors] = useState([]);
  const [originalSectors, setOriginalSectors] = useState([]);
  const [statistics, setStatistics] = useState({
    totalIndicators: 0,
    activeIndicators: 0,
    strongPillars: 0,
    weakPillars: 0,
    avgScore: 0,
    topPillar: null,
    bottomPillar: null,
    totalStocks: 0,
    totalMarketCap: 0,
    avgValScore: 0,
    attractiveSectors: 0,
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [scoresRes, weightsRes, valuationRes, marketRes, macroRes, forecastRes, driversRes] = await Promise.allSettled([
        indicatorsAPI.getPillarScores(),
        indicatorsAPI.getPillarWeights(),
        valuationsAPI.getSectorValuations(),
        stocksAPI.getStocks(),
        macroAPI.getCurrent(),
        forecastAPI.getMarketForecast(6).catch(() => ({ data: { forecast: [] } })),
        forecastAPI.getForecastDrivers().catch(() => null),
      ]);

      // Extract data from settled promises
      const scoresData = scoresRes.status === 'fulfilled' ? scoresRes.value.data : [];
      const weightsData = weightsRes.status === 'fulfilled' ? weightsRes.value.data : [];
      const valuationData = valuationRes.status === 'fulfilled' ? valuationRes.value.data : { sectors: [] };
      const marketData = marketRes.status === 'fulfilled' ? marketRes.value.data : { stocks: [] };
      const macroData = macroRes.status === 'fulfilled' ? macroRes.value.data : null;
      const forecastData = forecastRes.status === 'fulfilled' ? (forecastRes.value?.data || { forecast: [] }) : { forecast: [] };
      const driversData = driversRes.status === 'fulfilled' ? (driversRes.value?.data || null) : null;

      // Debug logging for failed requests
      if (scoresRes.status === 'rejected') {
        console.error('Failed to fetch pillar scores:', scoresRes.reason);
      }
      if (weightsRes.status === 'rejected') {
        console.error('Failed to fetch pillar weights:', weightsRes.reason);
      }
      if (macroRes.status === 'rejected') {
        console.error('Failed to fetch macro data:', macroRes.reason);
      }

      setPillarScores(scoresData);
      setTopSectors([]);
      setBottomSectors([]);
      setMomentum({ rising_sectors: [], falling_sectors: [] });
      setMarketBreadth(null);
      setForecast(forecastData.forecast || []);
      setForecastDrivers(driversData);
      setAllSectors([]);
      
      if (macroData) {
        setMacroScore(macroData);
      }
      

      const weightsMap = {};
      weightsData.forEach(item => {
        weightsMap[item.pillar_code] = {
          weight: item.pillar_weight,
          name: item.pillar_name,
        };
      });
      setPillarWeights(weightsMap);

      // Calculate market score
      const score = scoresData.reduce((sum, item) => {
        const weight = weightsMap[item.pillar]?.weight || 0;
        return sum + (item.normalized_score * weight);
      }, 0);

      // Calculate statistics
      const totalIndicators = scoresData.reduce((sum, item) => sum + item.indicators_count, 0);
      const activeIndicators = scoresData.reduce((sum, item) => sum + item.indicators_available, 0);
      const strongPillars = scoresData.filter(item => item.normalized_score >= 70).length;
      const weakPillars = scoresData.filter(item => item.normalized_score < 50).length;
      const avgScore = scoresData.reduce((sum, item) => sum + item.normalized_score, 0) / scoresData.length;
      
      // Find top and bottom pillars
      const sortedPillars = [...scoresData].sort((a, b) => b.normalized_score - a.normalized_score);
      const topPillar = sortedPillars[0];
      const bottomPillar = sortedPillars[sortedPillars.length - 1];

      // Market statistics
      const totalStocks = marketData.stocks.length;
      const totalMarketCap = marketData.stocks.reduce((sum, s) => sum + (s.market_cap_bn || 0), 0);

      // Helper function to determine recommendation considering quadrant
      const getRecommendation = (sector, avgValuation, momentumMap, topPotential) => {
        const finalScore = parseFloat(sector.final_score) || 0;
        const valScore = parseFloat(sector.avg_val_score) || 50;
        const scoreChange = momentumMap.get(sector.valuation_sector) || 0;
        
        // Determine quadrant
        const isHighVal = valScore >= avgValuation;
        const isRising = scoreChange > 0;
        const isCautionZone = !isHighVal && !isRising; // Low valuation + Falling momentum
        
        // Base recommendation on final score
        let recommendation;
        if (finalScore >= 60) {
          recommendation = t('rankings.strongBuy');
        } else if (score >= 70) {
          recommendation = t('rankings.buy');
        } else if (score >= 50) {
          recommendation = t('rankings.hold');
        } else {
          if (topPotential) {
            recommendation = t('rankings.increaseWeight');
          } else {
            recommendation = t('rankings.underweight');
          }
        }
        
        // Override: No Strong Buy in Caution Zone
        if (recommendation === t('rankings.strongBuy') && isCautionZone) {
          recommendation = t('rankings.buy'); // Downgrade to Buy
        }

        return recommendation;
      };

      // Valuation statistics
      const avgValScore = valuationData.sectors.reduce((sum, s) => sum + s.avg_val_score, 0) / valuationData.sectors.length;
      const attractiveSectors = valuationData.sectors.filter(s => s.avg_val_score >= 70).length;

      // Calculate momentum data based on sector rankings
      // Note: Real momentum calculation requires historical data comparison
      // For now, we'll use final_score as proxy for momentum
      const momentumData = { rising_sectors: [], falling_sectors: [] };
      
      // Sort sectors by final_score to identify rising/falling
      const sortedByScore = [...valuationData.sectors].sort((a, b) => b.final_score - a.final_score);
      
      sortedByScore.forEach((sector, index) => {
        const momentumItem = {
          sector_name: sector.sector_code || sector.valuation_sector,
          sector_name_en: sector.sector_name_en || sector.valuation_sector,
          sector_name_vi: sector.sector_name_vi || sector.valuation_sector,
          score_change: sector.final_score - 50, // Relative to neutral (50)
          rank_change: 0, // TODO: Implement historical comparison
          final_score: sector.final_score,
          ranking: sector.ranking
        };
        
        // Top half = rising, bottom half = falling
        if (index < sortedByScore.length / 2) {
          momentumData.rising_sectors.push(momentumItem);
        } else {
          momentumData.falling_sectors.push(momentumItem);
        }
      });

      // Already sorted by final_score
      // No need to sort again

      // Create momentum map for quadrant determination
      const momentumMap = new Map();
      if (momentumData) {
        [...(momentumData.rising_sectors || []), ...(momentumData.falling_sectors || [])].forEach(m => {
          momentumMap.set(m.sector_name, m.score_change || 0);
        });
      }

      // Process sector rankings for TopSectorsCards
      const sortedSectors = [...valuationData.sectors].sort((a, b) => b.final_score - a.final_score);
      const topSectorsData = sortedSectors.slice(0, 3).map(sector => ({
        sector_name: sector.valuation_sector,
        sector_name_en: sector.sector_name_en || sector.valuation_sector,
        sector_name_vi: sector.sector_name_vi || sector.valuation_sector,
        tailwind_score: sector.tailwind_score,
        val_score: sector.val_adjustment,
        final_score: sector.final_score,
        ranking: sector.ranking,
        recommendation: getRecommendation(sector, avgValScore, momentumMap, true)
      }));
      const bottomSectorsData = sortedSectors.slice(-3).map(sector => ({
        sector_name: sector.valuation_sector,
        sector_name_en: sector.sector_name_en || sector.valuation_sector,
        sector_name_vi: sector.sector_name_vi || sector.valuation_sector,
        tailwind_score: sector.tailwind_score,
        val_score: sector.val_adjustment,
        final_score: sector.final_score,
        ranking: sector.ranking,
        recommendation: getRecommendation(sector, avgValScore, momentumMap, false)
      }));

      setTopSectors(topSectorsData);
      setBottomSectors(bottomSectorsData);
      
      // Transform sectors data for ValuationMomentumMatrix component
      const transformedSectors = valuationData.sectors.map(sector => ({
        sector_name: sector.valuation_sector,
        sector_name_en: sector.sector_name_en || sector.valuation_sector,
        sector_name_vi: sector.sector_name_vi || sector.valuation_sector,
        val_score: parseFloat(sector.avg_val_score) || 50,
        final_score: parseFloat(sector.final_score) || 0,
        recommendation: getRecommendation(sector, avgValScore, momentumMap),
        // Include pillar scores for PerformanceAttribution component
        pillar_a_score: sector.pillar_a_score,
        pillar_b_score: sector.pillar_b_score,
        pillar_c_score: sector.pillar_c_score,
        pillar_d_score: sector.pillar_d_score,
        pillar_e_score: sector.pillar_e_score,
        pillar_f_score: sector.pillar_f_score,
        pillar_g_score: sector.pillar_g_score,
        pillar_h_score: sector.pillar_h_score,
        val_adjustment: sector.val_adjustment,
      }));
      
      setAllSectors(transformedSectors);
      setOriginalSectors(valuationData.sectors);

      setMomentum(momentumData);

      setStatistics({
        totalIndicators,
        activeIndicators,
        strongPillars,
        weakPillars,
        avgScore,
        topPillar,
        bottomPillar,
        totalStocks,
        totalMarketCap,
        avgValScore,
        attractiveSectors,
      });

      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) {
    return <DashboardSkeleton />;
  }

  // Enhanced chart data with pillar names and colors
  const getScoreColor = (score) => {
    if (score >= 70) return '#11998e'; // Strong - Green
    if (score >= 60) return '#667eea'; // Good - Blue
    if (score >= 50) return '#ffa726'; // Neutral - Orange
    return '#ff6b6b'; // Weak - Red
  };

  const chartData = pillarScores.map(item => {
    const pillarInfo = pillarWeights[item.pillar];
    return {
      pillar: item.pillar,
      pillarName: pillarInfo?.name || item.pillar,
      pillarNameShort: pillarInfo?.name?.split('/')[0]?.trim() || item.pillar,
      score: item.normalized_score,
      weighted: item.normalized_score * (pillarInfo?.weight || 0),
      weight: (pillarInfo?.weight || 0) * 100,
      color: getScoreColor(item.normalized_score),
      indicatorsCount: item.indicators_count,
    };
  });

  const radarData = pillarScores.map(item => {
    const pillarInfo = pillarWeights[item.pillar];
    return {
      subject: item.pillar,
      pillarName: pillarInfo?.name?.split('/')[0]?.trim() || item.pillar,
      value: item.normalized_score,
      fullValue: item.normalized_score,
    };
  });

  return (
    <Box sx={{ width: '100%', maxWidth: '100%' }}>
      {/* Page Header */}
      <Box sx={{ mb: 4, display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: '#fff',
            boxShadow: '0 4px 16px 0 rgba(102,126,234,0.10)',
            mr: 3,
          }}
        >
          <AutoAwesomeIcon fontSize="large" />
        </Box>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            {t('dashboard.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('dashboard.subtitle')}
          </Typography>
        </Box>
      </Box>

      {/* Macro Summary Chart (NEW) */}
      <MacroSummaryChart macroScore={macroScore} />

      {/* NEW: Macro Score Trend */}
      <MacroScoreTrend currentScore={macroScore} />

      {/* NEW: AI News Impact Dashboard */}
      <AINewsImpactDashboard />

      {/* Risk Management Widget */}
      <Box sx={{ mb: 3 }}>
        <RiskManagementWidget />
      </Box>

      {/* Top Investment Opportunities */}
      <TopSectorsCards topSectors={topSectors} bottomSectors={bottomSectors} />

      {/* Risk Monitor & Market Breadth Section */}
      <RiskAndBreadthSection macroScore={macroScore} marketBreadth={marketBreadth} />

      {/* Sector Momentum */}
      <SectorMomentumCards momentum={momentum} />

      {/* Charts Section */}
      <ChartsSection 
        chartData={chartData} 
        radarData={radarData} 
        statistics={statistics}
        getScoreColor={getScoreColor}
      />

      {/* Market Forecast Section */}
      <MarketForecastChart forecast={forecast} forecastDrivers={forecastDrivers} />

      {/* NEW: Indicator Heatmap with AI Badges */}
      <IndicatorHeatmap showAIBadges={true} />

      {/* Advanced Reports Section */}
      <Box sx={{ mt: 4, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          {t('dashboard.advancedAnalytics.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t('dashboard.advancedAnalytics.subtitle')}
        </Typography>
      </Box>

      {/* Risk-Reward Analysis */}
      <RiskRewardScatterPlot sectors={originalSectors} />

      {/* Valuation vs Momentum Matrix */}
      <ValuationMomentumMatrix sectors={allSectors} momentum={momentum} />

      {/* Sector Rotation Tracker */}
      <SectorRotationTracker momentum={momentum} />

      {/* Pillar Trend Analysis */}
      <PillarTrendAnalysis />

      {/* Performance Attribution */}
      <PerformanceAttribution sectors={allSectors} />
    </Box>
  );
}

export default Dashboard;
